import { useState, useMemo, useEffect } from 'react';
import { Tabs, Tab, Button, Card, Alert } from 'react-bootstrap';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { RJSFSchema, IconButtonProps } from '@rjsf/utils';
import { Environment } from '../types';
import { CustomizationTabs } from './CustomizationTabs';
import { ApiService } from '../services/api';
import './ValidationStyles.css';

// Custom button templates for better styling
function AddButton(props: IconButtonProps) {
  return (
    <Button
      variant="success"
      size="sm"
      onClick={props.onClick}
      disabled={props.disabled}
      className="mt-2"
    >
      <i className="bi bi-plus-circle me-1"></i>
      Add {props.title || 'Item'}
    </Button>
  );
}

function RemoveButton(props: IconButtonProps) {
  return (
    <Button
      variant="danger"
      size="sm"
      onClick={props.onClick}
      disabled={props.disabled}
      className="ms-2"
    >
      <i className="bi bi-trash me-1"></i>
      Remove
    </Button>
  );
}

// Custom array field template for better visual separation
function ArrayFieldTemplate(props: any) {
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-bold">{props.title}</label>
        {props.canAdd && (
          <Button
            variant="success"
            size="sm"
            onClick={props.onAddClick}
            disabled={props.disabled || props.readonly}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add {props.title}
          </Button>
        )}
      </div>
      {props.items && props.items.length > 0 && (
        <div className="border rounded p-3 bg-light">
          {props.items.map((element: any) => (
            <Card key={element.key} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">{element.children}</div>
                  {element.hasRemove && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={element.onDropIndexClick(element.index)}
                      disabled={element.disabled || element.readonly}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
      {props.items && props.items.length === 0 && (
        <div className="text-muted fst-italic">
          No {props.title?.toLowerCase()} added yet. Click "Add {props.title}" to create one.
        </div>
      )}
    </div>
  );
}

// Custom object field template for nested objects
function ObjectFieldTemplate(props: any) {
  const isTopLevel = props.idSchema.$id === 'root';
  
  if (isTopLevel) {
    return <div>{props.properties.map((prop: any) => prop.content)}</div>;
  }

  return (
    <Card className="mb-3">
      <Card.Header className="bg-white">
        <strong>{props.title}</strong>
        {props.description && (
          <small className="text-muted d-block">{props.description}</small>
        )}
      </Card.Header>
      <Card.Body>
        {props.properties.map((prop: any) => prop.content)}
      </Card.Body>
    </Card>
  );
}

// Custom field template to show inline validation errors
function FieldTemplate(props: any) {
  const {
    id,
    classNames,
    label,
    help,
    required,
    errors,
    children,
    displayLabel,
    rawErrors,
  } = props;

  // Check for errors in multiple ways (RJSF can pass errors differently)
  const errorList = rawErrors || (errors && errors.props && errors.props.errors) || [];
  const hasErrors = errorList && errorList.length > 0;
  const fieldClassName = `form-group ${classNames} ${hasErrors ? 'field-error' : ''}`;

  return (
    <div className={fieldClassName}>
      {displayLabel && label && (
        <label htmlFor={id} className={`form-label ${required ? 'required' : ''}`}>
          {label}
        </label>
      )}
      {children}
      {hasErrors && (
        <div className="field-error-message">
          {errorList.map((error: string, index: number) => (
            <div key={index} className="error-detail">
              {error}
            </div>
          ))}
        </div>
      )}
      {help}
    </div>
  );
}

const customTemplates = {
  ButtonTemplates: {
    AddButton,
    RemoveButton,
  },
  ArrayFieldTemplate,
  ObjectFieldTemplate,
  FieldTemplate,
};

interface EnvironmentTabsProps {
  environments: Environment[];
  environmentSchema: RJSFSchema;
  workloadName: string;
  onChange: (environments: Environment[]) => void;
  disabled?: boolean;
  readonly?: boolean;
  showValidation?: boolean;
}

export function EnvironmentTabs({
  environments,
  environmentSchema,
  workloadName,
  onChange,
  disabled,
  readonly,
  showValidation = false,
}: EnvironmentTabsProps) {
  const [activeTab, setActiveTab] = useState<string>('0');
  const [organizationalUnits, setOrganizationalUnits] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<number, { accountName?: string; accountEmail?: string }>>({});
  const [validationTimeouts, setValidationTimeouts] = useState<Record<string, number>>({});

  // Load organizational units on mount
  useEffect(() => {
    const loadOUs = async () => {
      try {
        const ous = await ApiService.getOrganizationalUnits();
        setOrganizationalUnits(ous);
      } catch (error) {
        console.error('Failed to load organizational units:', error);
      }
    };
    loadOUs();
  }, []);

  // Update all environment account names when workloadName changes
  useEffect(() => {
    if (!workloadName) return;
    
    const updatedEnvironments = environments.map(env => {
      // Only update if vending new account (no accountId) and has environmentType
      if (env.environmentType && !env.accountId) {
        const expectedName = `${workloadName}-${env.environmentType}`;
        const expectedEmail = `rdglzstaging+${workloadName}-${env.environmentType}@raildeliverygroup.com`;
        
        // Only update if it looks like it was auto-generated (matches pattern or is empty)
        const shouldUpdateName = !env.accountName || env.accountName.includes('-');
        const shouldUpdateEmail = !env.accountEmail || env.accountEmail.includes('rdglzstaging+');
        
        return {
          ...env,
          accountName: shouldUpdateName ? expectedName : env.accountName,
          accountEmail: shouldUpdateEmail ? expectedEmail : env.accountEmail,
        };
      }
      return env;
    });
    
    // Only trigger onChange if something actually changed
    const hasChanges = updatedEnvironments.some((env, idx) => 
      env.accountName !== environments[idx].accountName || 
      env.accountEmail !== environments[idx].accountEmail
    );
    
    if (hasChanges) {
      onChange(updatedEnvironments);
    }
  }, [workloadName]); // Only depend on workloadName, not environments to avoid loops

  // Extract customization schema and create modified environment schema without customizations
  // Also add OU enum dynamically
  const { modifiedEnvSchema, customizationSchema } = useMemo(() => {
    const custSchema = (environmentSchema as any).properties?.customizations?.items;
    
    const modifiedSchema = { ...environmentSchema };
    if ((modifiedSchema as any).properties) {
      const { customizations, ...otherProps } = (modifiedSchema as any).properties;
      (modifiedSchema as any).properties = otherProps;
      
      // Add enum for organizationalUnit if we have OUs loaded
      if (organizationalUnits.length > 0 && (modifiedSchema as any).properties.organizationalUnit) {
        (modifiedSchema as any).properties.organizationalUnit = {
          ...(modifiedSchema as any).properties.organizationalUnit,
          enum: organizationalUnits,
        };
      }
    }
    if ((modifiedSchema as any).required) {
      (modifiedSchema as any).required = (modifiedSchema as any).required.filter(
        (field: string) => field !== 'customizations'
      );
    }
    
    return {
      modifiedEnvSchema: modifiedSchema as RJSFSchema,
      customizationSchema: custSchema as RJSFSchema | undefined,
    };
  }, [environmentSchema, organizationalUnits]);

  const handleAddEnvironment = () => {
    // Clone the last environment if it exists
    const lastEnv = environments[environments.length - 1];
    
    const newEnvironment: any = lastEnv ? {
      ...lastEnv, // Copy everything from last environment
      environmentType: '', // User must select this
      accountId: '', // Never clone accountId - always empty for new accounts
      accountName: '', // Will be auto-populated when environmentType is selected
      accountEmail: '', // Will be auto-populated when environmentType is selected
      customizations: lastEnv.customizations ? 
        JSON.parse(JSON.stringify(lastEnv.customizations)) : [] // Deep clone customizations
    } : {
      // Fallback to empty if no previous environment
      accountId: '',
      region: '',
    };
    
    const newEnvironments = [...environments, newEnvironment];
    onChange(newEnvironments);
    setActiveTab(String(newEnvironments.length - 1));
  };

  const handleRemoveEnvironment = (index: number) => {
    const newEnvironments = environments.filter((_, i) => i !== index);
    onChange(newEnvironments);
    
    // Adjust active tab if needed
    const newIndex = Math.min(index, newEnvironments.length - 1);
    if (newIndex >= 0) {
      setActiveTab(String(newIndex));
    }
  };

  const handleEnvironmentChange = (index: number, data: Environment) => {
    const newEnvironments = [...environments];
    const oldEnv = newEnvironments[index];
    
    // Auto-populate account name and email when environmentType is selected
    if (data.environmentType && !data.accountId && workloadName) {
      // Only auto-populate if fields are empty or match the old pattern
      const expectedOldName = oldEnv.environmentType ? `${workloadName}-${oldEnv.environmentType}` : '';
      const expectedOldEmail = oldEnv.environmentType ? `rdglzstaging+${workloadName}-${oldEnv.environmentType}@raildeliverygroup.com` : '';
      
      if (!data.accountName || data.accountName === expectedOldName || data.accountName === '') {
        data.accountName = `${workloadName}-${data.environmentType}`;
      }
      
      if (!data.accountEmail || data.accountEmail === expectedOldEmail || data.accountEmail === '') {
        data.accountEmail = `rdglzstaging+${workloadName}-${data.environmentType}@raildeliverygroup.com`;
      }
    }
    
    // Update domain names in customizations when environmentType is set or changes
    if (data.environmentType && data.customizations) {
      // Find what environment type the domains currently have
      // Check if oldEnv has an environmentType, or try to detect from domain pattern
      let sourceEnvType = oldEnv.environmentType;
      
      // If no old env type (new environment), try to detect from domain
      if (!sourceEnvType && data.customizations.length > 0) {
        const firstCustomization = data.customizations[0];
        if (firstCustomization.parameters?.domains && firstCustomization.parameters.domains.length > 0) {
          const domain = firstCustomization.parameters.domains[0];
          // Try to extract env type from domain pattern: something.{envType}.something
          const match = domain.match(/\.([a-z]+)\./);
          if (match && ['dev', 'test', 'uat', 'preprod', 'prod'].includes(match[1])) {
            sourceEnvType = match[1];
          }
        }
      }
      
      // Only replace if we have a source env type and it's different from the new one
      if (sourceEnvType && sourceEnvType !== data.environmentType) {
        data.customizations = data.customizations.map(customization => {
          const updatedCustomization = { ...customization };
          
          if (updatedCustomization.parameters) {
            // Update domainName field
            if (updatedCustomization.parameters.domainName) {
              updatedCustomization.parameters.domainName = 
                updatedCustomization.parameters.domainName.replace(
                  `.${sourceEnvType}.`,
                  `.${data.environmentType}.`
                );
            }
            
            // Update domains array
            if (updatedCustomization.parameters.domains && Array.isArray(updatedCustomization.parameters.domains)) {
              updatedCustomization.parameters.domains = 
                updatedCustomization.parameters.domains.map((domain: string) =>
                  domain.replace(`.${sourceEnvType}.`, `.${data.environmentType}.`)
                );
            }
          }
          
          return updatedCustomization;
        });
      }
    }
    
    newEnvironments[index] = data;
    onChange(newEnvironments);

    // Trigger validation for account vending fields with debounce
    const env = data as any;
    if (!env.accountId) {
      // Only validate if accountId is not provided (account vending mode)
      if (env.accountName !== (oldEnv as any)?.accountName || env.accountEmail !== (oldEnv as any)?.accountEmail) {
        // Clear existing timeout
        const timeoutKey = `validation-${index}`;
        if (validationTimeouts[timeoutKey]) {
          clearTimeout(validationTimeouts[timeoutKey]);
        }
        
        // Set new timeout for validation
        const timeout = setTimeout(async () => {
          try {
            const result = await ApiService.validateAccount(env.accountName, env.accountEmail);
            setValidationErrors(prev => ({
              ...prev,
              [index]: result.errors || {},
            }));
          } catch (error) {
            console.error('Validation failed:', error);
          }
        }, 500) as unknown as number; // 500ms debounce
        
        setValidationTimeouts(prev => ({
          ...prev,
          [timeoutKey]: timeout,
        }));
      }
    } else {
      // Clear validation errors if accountId is provided
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const getEnvironmentLabel = (env: Environment, index: number) => {
    if (env.environmentType) {
      return `${env.environmentType.toUpperCase()}`;
    }
    return `Environment ${index + 1}`;
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <label className="form-label fw-bold mb-0">Environments</label>
        <Button
          variant="success"
          size="sm"
          onClick={handleAddEnvironment}
          disabled={disabled || readonly}
        >
          <i className="bi bi-plus-circle me-1"></i>
          Add Environment
        </Button>
      </div>

      {environments.length === 0 ? (
        <div className="text-muted fst-italic border rounded p-3 bg-light">
          No environments added yet. Click "Add Environment" to create one.
        </div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => k && setActiveTab(k)}
              className="border-bottom"
            >
              {environments.map((env, index) => (
                <Tab
                  key={`${env.environmentType || 'new'}-${index}`}
                  eventKey={String(index)}
                  title={getEnvironmentLabel(env, index)}
                >
                  <div className="p-3">
                    {!readonly && (
                      <div className="d-flex justify-content-end mb-3">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveEnvironment(index)}
                          disabled={disabled}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Remove Environment
                        </Button>
                      </div>
                    )}
                    
                    {/* Account Vending Info Alert */}
                    {!(env as any).accountId && (
                      <Alert variant="info" className="mb-3">
                        <Alert.Heading>
                          <i className="bi bi-info-circle me-2"></i>
                          Account Vending Mode
                        </Alert.Heading>
                        <p className="mb-0">
                          Since no Account ID is provided, a new AWS account will be automatically created through LZA.
                          Please provide the account name, email, and organizational unit below.
                        </p>
                      </Alert>
                    )}

                    {/* Validation Errors */}
                    {validationErrors[index] && Object.keys(validationErrors[index]).length > 0 && (
                      <Alert variant="danger" className="mb-3">
                        <Alert.Heading>
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Validation Errors
                        </Alert.Heading>
                        <ul className="mb-0">
                          {validationErrors[index].accountName && (
                            <li><strong>Account Name:</strong> {validationErrors[index].accountName}</li>
                          )}
                          {validationErrors[index].accountEmail && (
                            <li><strong>Account Email:</strong> {validationErrors[index].accountEmail}</li>
                          )}
                        </ul>
                      </Alert>
                    )}

                    <Form
                      schema={modifiedEnvSchema}
                      uiSchema={{
                        'ui:order': ['environmentType', 'region', 'accountId', 'accountName', 'accountEmail', 'organizationalUnit'],
                        accountId: {
                          'ui:placeholder': 'Leave empty for account vending',
                          'ui:emptyValue': ''
                        },
                        customizations: {
                          'ui:widget': 'hidden'
                        }
                      }}
                      formData={env}
                      validator={validator}
                      onChange={(data) => {
                        if (data.formData) {
                          handleEnvironmentChange(index, data.formData);
                        }
                      }}
                      templates={customTemplates}
                      disabled={disabled}
                      readonly={readonly}
                      liveValidate={showValidation}
                      showErrorList={false}
                      noHtml5Validate
                    >
                      <div></div>
                    </Form>

                    {/* Separate customizations section */}
                    {customizationSchema && (
                      <CustomizationTabs
                        customizations={env.customizations || []}
                        customizationSchema={customizationSchema}
                        onChange={(customizations) => {
                          const updatedEnv = { ...env, customizations };
                          handleEnvironmentChange(index, updatedEnv);
                        }}
                        disabled={disabled}
                        readonly={readonly}
                        showValidation={showValidation}
                      />
                    )}
                  </div>
                </Tab>
              ))}
            </Tabs>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
